import dayjs from "dayjs";
import { Field, useFormikContext } from "formik";
import { motion } from "framer-motion";
import React, { FC, useState } from "react";
import { OrderFormValues } from "../lib/contexts/FormikContext";
import { Product, Useplace } from "../lib/types";
import { calculateSum, formatCurrency } from "../lib/utils";

interface Props {
  currentStep: number;
  setCurrentStep: (s: number) => void;
  selectedProducts: Product[];
  address: Useplace;
}
export const Summary: FC<Props> = ({
  currentStep,
  setCurrentStep,
  selectedProducts,
  address,
}) => {
  const [summaryError, setSummaryError] = useState<string>();
  const errorInputClasses = "border border-red-500";
  const errorMsgClasses = "text-red-500 text-sm";
  const { values } = useFormikContext<OrderFormValues>();

  if (currentStep < 2) return null;
  if (currentStep > 2)
    return (
      <motion.div
        layout
        className="w-full shadow-xl p-8 bg-[#374F5A] text-white"
      >
        <div className="flex flex-row justify-between items-end">
          <div />
          <div className="">
            <button
              className=" rounded-full px-4 py-1 inline-block text-[#374F5A] bg-white"
              onClick={() => setCurrentStep(2)}
            >
              Visa sammanställning
            </button>
          </div>
        </div>
      </motion.div>
    );
  return (
    <div>
      {/* TODO: If plural */}

      <div className="flex flex-row gap-8 items-center mb-4">
        {/* <FontAwesomeIcon icon={faLocationDot} className="text-4xl" /> */}
        <i
          aria-hidden="true"
          className="heroicons heroicons-map-pin text-secondary-500 text-4xl "
        />

        <h3 className="text-base tracking-wide font-sans font-bold">
          Beställningen avser följade adress
        </h3>
      </div>
      <div className="flex flex-row mb-8">
        <div>
          <strong>Adress</strong>
          <br />
          {`${address?.address}
              ${address?.streetno}${address?.floor}, ${
            address?.apartment ? `lgh ${address?.apartment} ,` : ""
          } ${address?.zip} ${address?.city}`}
        </div>
        <div className="ml-8">
          <strong>Fastighetsbeteckning</strong>
          <br />
          {address?.site.propertyid}
        </div>
      </div>
      <hr className="my-4" />

      {/* PRODUKT */}
      <div className="flex flex-row gap-8 items-center mb-4">
        <i className="text-4xl heroicons heroicons-shopping-cart text-secondary-500" />

        <h3 className="text-base tracking-wide font-sans font-bold">
          {selectedProducts.length > 1 ? "Valda produkter" : "Vald produkt"}
        </h3>
      </div>

      <div className="mb-8">
        <div className="mb-8 w-full grid grid-cols-2">
          <div>
            <strong>Produkt</strong>
          </div>
          <div>
            <strong>Pris</strong>
          </div>
          {selectedProducts.map((p) => (
            <React.Fragment key={p.id}>
              <div key={p.id}>
                {p.title} {p.title2}
              </div>
              <div>
                {p.product_prices.length > 0 ? (
                  <>
                    {p.product_prices[0].price.price === 0 ? (
                      <>Kostnadsfritt</>
                    ) : (
                      <>
                        {p.product_prices[0].price.varying_price && "Från "}
                        {formatCurrency(p.product_prices[0].price.price)}
                      </>
                    )}
                    {p.product_prices[0].price.interval === 1 && " / mån"}
                    {p.product_prices[0].price.interval === 12 && " / år"}
                  </>
                ) : (
                  <>pris saknas</>
                )}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
      <hr className="my-4" />
      {/* Totalkostnad */}
      {/*
      installation
      lime
      loan
       */}
      {(selectedProducts[0].orderflow_type === "installation" ||
        selectedProducts[0].orderflow_type === "loan") && (
        <>
          <div className="flex flex-row gap-8 items-center mb-4">
            <i className="text-4xl heroicons heroicons-banknotes text-secondary-500" />

            <h3 className="text-base tracking-wide font-sans font-bold">
              Totalkostnader
            </h3>
          </div>
          <div className="mb-2 italic">
            Här har vi sammanställt samtliga kostander för dina valda produkter.
          </div>
          <div className="mb-8 w-full grid grid-cols-2 ">
            <div>
              <strong>Engångkostnad</strong>
            </div>
            <div>
              {selectedProducts.find(
                (sp) => sp.product_prices[0].price.varying_price === true
              ) && "Från "}
              {formatCurrency(calculateSum(selectedProducts, 0))}
            </div>

            <div>
              <strong>Månadskostnad</strong>
            </div>
            <div>
              {formatCurrency(calculateSum(selectedProducts, 1))} / månad
            </div>
            {calculateSum(selectedProducts, 12) > 0 && (
              <>
                <div>
                  <strong>Årsavgift</strong>
                </div>
                <div>{formatCurrency(calculateSum(selectedProducts, 12))}</div>
              </>
            )}
          </div>
          <hr className="my-4" />
        </>
      )}
      {selectedProducts[0].orderflow_type === "lime" && (
        <>
          <div className="flex flex-row gap-8 items-center mb-4">
            <i className="text-4xl heroicons heroicons-banknotes text-secondary-500" />

            <h3 className="text-base tracking-wide font-sans font-bold">
              Totalkostnader
            </h3>
          </div>
          <div className="mb-4 italic rounded shadow-xl bg-gray-50 p-4 flex flex-row gap-8 ">
            {/* <div className="flex items-center justify-center"> */}
            <i className="text-3xl fas fa-info-circle text-secondary-500" />
            {/* </div> */}
            <p>
              Vi kan inte presentera en totalkostnad för den valda produkten.
              När du skickat in din förfrågan kommer vi att kontakta dig för att
              plannera omfattning av önskat arbete. I samband med det kommer du
              att få ett avtalsförslag med priser och leveranstid.
            </p>
          </div>
          <hr className="my-4" />
        </>
      )}
      {/* BETEALNING */}
      {calculateSum(selectedProducts, 0) +
        (calculateSum(selectedProducts, 1) +
          calculateSum(selectedProducts, 12)) >
        0 && (
        <>
          <div className="flex flex-row gap-8 items-center mb-4">
            <i className="text-4xl heroicons heroicons-information-circle text-secondary-500" />

            <h3 className="text-base tracking-wide font-sans font-bold">
              Betalning
            </h3>
          </div>

          <div className="mb-8">
            <div className="mb-1 font-bold">Betalningsalternativ</div>
            <div className="mb-3">
              <div className="font-medium">Faktura</div>
              <p>
                För alla våra tjänster skickar vi en faktura, för vilken det
                tillkommer en faktureringsavgift på 29 kr per papersfaktura. Gör
                du en anmälan om e-faktura hos din internetbank tillkommer ingen
                faktureringsavgift.
              </p>
            </div>

            <div className="mb-1 font-bold">Faktureringsintervall(er)</div>
            {selectedProducts.map((sp) => (
              <div className="mb-3">
                <div className="font-medium">
                  {sp.title + " " + (sp.title2 || "")}
                </div>
                <p>{sp.orderflow_paymentdetails}</p>
              </div>
            ))}
          </div>
          <hr className="my-4" />
        </>
      )}
      {/* LEVERANS */}
      {selectedProducts[0].orderflow_type !== "lime" && (
        <>
          <div className="flex flex-row gap-8 items-center mb-4">
            <i className="text-4xl heroicons heroicons-truck text-secondary-500" />

            <h3 className="text-base tracking-wide font-sans font-bold">
              Leverans
            </h3>
          </div>
          <div className="mb-3 italic">
            Angivna leveransdatum är preliminära tills du har mottagit en
            beställningsbekräftelse från oss.
          </div>

          <div className="mb-8 grid grid-cols-2">
            {selectedProducts.map((sp, idx) => {
              if (sp.title.toLowerCase().includes("serviceavtal")) {
                if (idx > 0) {
                  return (
                    <React.Fragment key={sp.id}>
                      <div>
                        <strong>{sp.title + " " + (sp.title2 || "")}</strong>
                      </div>
                      <div>När fiberinstallationen är färdigställd.</div>
                    </React.Fragment>
                  );
                } else {
                  return (
                    <React.Fragment key={sp.id}>
                      <div>
                        <strong>{sp.title + " " + (sp.title2 || "")}</strong>
                      </div>
                      <div>
                        Önskat startdatum{" "}
                        {dayjs(values.startdate || null).format("YYYY-MM-DD")}
                      </div>
                    </React.Fragment>
                  );
                }
              } else {
                return (
                  <React.Fragment key={sp.id}>
                    <div>
                      <strong>{sp.title + " " + (sp.title2 || "")}</strong>
                    </div>
                    <div>{sp.delivery_public}</div>
                  </React.Fragment>
                );
              }
            })}
          </div>
          <hr className="my-4" />
        </>
      )}
      {selectedProducts[0].orderflow_type === "lime" && (
        <>
          <div className="flex flex-row gap-8 items-center mb-4">
            <i className="text-4xl heroicons heroicons-truck text-secondary-500" />

            <h3 className="text-base tracking-wide font-sans font-bold">
              Leverans
            </h3>
          </div>
          <div className="mb-4 italic rounded shadow-xl bg-gray-50 p-4 flex flex-row gap-8 ">
            {/* <div className="flex items-center justify-center"> */}
            <i className="text-3xl fas fa-info-circle text-secondary-500" />
            {/* </div> */}
            <p>
              Vi kan inte presentera en leveranstid för den valda produkten. När
              du skickat in din förfrågan kommer vi att kontakta dig för att
              plannera omfattning av önskat arbete. I samband med det kommer vi
              att meddela tillgänglighet för att utföra arbetet.
            </p>
          </div>
          <hr className="my-4" />
        </>
      )}
      {/* DIN UPPGIFTER */}
      <div className="flex flex-row gap-8 items-center mb-4">
        <i className="text-4xl heroicons heroicons-identification text-secondary-500" />

        <h3 className="text-base tracking-wide font-sans font-bold">
          Dina uppgifter
        </h3>
      </div>
      <div className="mb-8 grid grid-cols-2">
        <div>
          <strong>Namn och adress</strong>
          <br />
          {`${values.firstname} ${values.lastname}`}
          <br />
          {values.address_co && (
            <>
              c/o {values.address_co}
              <br />
            </>
          )}
          {`${values.address_street} ${values.address_streetno}${
            values.address_letter
          }${
            values.address_apartment ? ", Lgh. " + values.address_apartment : ""
          }`}
          <br />
          {`${values.address_zip} ${values.address_city}`}
        </div>
        <div className="grid grid-cols-2">
          <div>
            <strong>Personnummer</strong>
          </div>
          <div>{values.personalnumber}</div>
          <div>
            <strong>Epostadress</strong>
          </div>
          <div>{values.email}</div>
          <div>
            <strong>Mobilnummer</strong>
          </div>
          <div>{values.mobile}</div>
        </div>
        {values.message && (
          <div className="mt-4">
            <strong>Ditt meddelande</strong>
            <br />
            {values.message}
          </div>
        )}
      </div>
      {/* {values.startdate && (
        <div className="mb-8">
          <h3 className="text-lg mb-4">Önskat startdatum för serviceavtal</h3>
          {dayjs(values.startdate).format("YYYY-MM-DD")}
        </div>
      )} */}
      <hr className="my-4" />

      <h3 className="text-base tracking-wide font-sans font-bold mb-4">
        Godkännanden
      </h3>
      <div>
        {values.approval_owner && (
          <div className="mb-3">
            <i className="fa fa-check text-secondary-500" /> Jag intygar att jag
            är ägare av fastigheten som beställningen avser.
          </div>
        )}
        {values.approval_com_email && (
          <div className="mb-3">
            <i className="fa fa-check text-secondary-500" /> Jag tar gärna emot
            information om erbjudanden från Lunet på min e-postadress.
          </div>
        )}
        {values.approval_com_sms && (
          <div className="mb-6">
            <i className="fa fa-check text-secondary-500" /> Jag tar gärna emot
            information om erbjudanden från Lunet via SMS.
          </div>
        )}
      </div>
      <hr className="my-8" />
      <div className="mb-8 p-4 border border-[#374F5A] rounded-lg">
        <label className="flex items-center gap-2">
          <Field type="checkbox" name="approval_summary" />
          Jag har granskat uppgifterna och försäkrar att de är korrekta
        </label>
        {summaryError && <div className={errorMsgClasses}>{summaryError}</div>}
      </div>
      <button
        type="button"
        onClick={() => {
          if (values.approval_summary) {
            setSummaryError("");
            setCurrentStep(currentStep + 1);
          } else {
            setSummaryError(
              "Du måste markera att du har granskat uppgifterna och försäkrar att de är korrekta"
            );
          }
        }}
        style={{ backgroundColor: "#374F5A" }}
        className="rounded-full px-4 py-2 text-white inline-block  text-xs uppercase font-bold"
      >
        Gå vidare
      </button>
    </div>
  );
};
